import { Box, Flex, LightMode } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import {
  ClearIndicatorProps,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  MenuProps,
  MultiValueRemoveProps,
  chakraComponents
} from "chakra-react-select"
import { snakeCase } from "change-case"

export type CustomSelectProps = {
  icon?: IconName
  renderControlLeftElement?: () => JSX.Element
}

type WithCustomSelectProps<
  T extends any,
  TCustomSelectProps extends CustomSelectProps
> = T & {
  selectProps: TCustomSelectProps
}

export function getSelectCustomComponents<
  Option,
  IsMulti extends boolean,
  TCustomSelectProps extends CustomSelectProps = CustomSelectProps
>() {
  type Group = GroupBase<Option>

  const ClearIndicator = (
    props: WithCustomSelectProps<
      ClearIndicatorProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => (
    <chakraComponents.ClearIndicator {...props}>
      <Pathicon
        icon="x"
        size="1.4em"
        data-testid={snakeCase(
          `${props.selectProps["aria-label"] || ""} clear all`
        )}
      />
    </chakraComponents.ClearIndicator>
  )

  const DropdownIndicator = (
    props: WithCustomSelectProps<
      DropdownIndicatorProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => {
    if (
      props.selectProps.options?.length === 0 ||
      (props.selectProps.options?.length === 1 &&
        (props.selectProps.options[0] as { id: number }).id === -1)
    ) {
      // If there is only one option, which is the create option,
      // we don't want to show the dropdown indicator.
      // This treats the select input as more of a text input.
      return null
    }

    return (
      <chakraComponents.DropdownIndicator {...props}>
        <Pathicon icon="chevron-down" size="1em" />
      </chakraComponents.DropdownIndicator>
    )
  }

  const MultiValueRemove = (
    props: WithCustomSelectProps<
      MultiValueRemoveProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => (
    <chakraComponents.MultiValueRemove {...props}>
      <Flex cursor="pointer">
        <Pathicon icon="x" size=".85em" />
      </Flex>
    </chakraComponents.MultiValueRemove>
  )

  // Ensure we render the menu in light mode.
  const Menu = (
    props: WithCustomSelectProps<
      MenuProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => {
    if (
      props.selectProps.options?.length === 0 ||
      (props.selectProps.options?.length === 1 &&
        (props.selectProps.options[0] as { id: number }).id === -1)
    ) {
      // Hiding menu if there is only one option,
      // which in this case is presumed to be the create option.
      // This treats the select input as more of a text input.
      return null
    }

    return (
      <LightMode>
        <chakraComponents.Menu {...props} />
      </LightMode>
    )
  }

  const Control = (
    props: WithCustomSelectProps<
      ControlProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => {
    const iconElement = !!props.selectProps.icon && (
      <Pathicon icon={props.selectProps.icon} />
    )
    const leftElement =
      props.selectProps.renderControlLeftElement?.() || iconElement

    return (
      <chakraComponents.Control {...props}>
        {!!leftElement && (
          <Box
            width="var(--input-height)"
            height="var(--input-height)"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {leftElement}
          </Box>
        )}
        {props.children}
      </chakraComponents.Control>
    )
  }

  return {
    Menu,
    ClearIndicator,
    DropdownIndicator,
    MultiValueRemove,
    Control
  }
}
